import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency, formatPercent } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const TaxBracketCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [income, setIncome] = useState("85000");
  const [dependents, setDependents] = useState("0");
  const [itemized, setItemized] = useState("0");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.incomeTax({
        gross_income: Number(income),
        filing_status: filing,
        dependents: Number(dependents),
        itemized_deductions: Number(itemized),
      });
      setResult(res);
    } catch (e) {
      toast.error("Could not compute. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="bracket-filing" />
      </div>
      <div>
        <label htmlFor="gross-income" className="font-body text-sm font-semibold block mb-2">
          Gross Annual Income (USD)
        </label>
        <input
          id="gross-income"
          type="number"
          inputMode="decimal"
          value={income}
          onChange={(e) => setIncome(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-gross-income"
          placeholder="85000"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-body text-sm font-semibold block mb-2">Dependents</label>
          <input
            type="number"
            inputMode="numeric"
            value={dependents}
            onChange={(e) => setDependents(e.target.value)}
            className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
            data-testid="input-dependents"
          />
        </div>
        <div>
          <label className="font-body text-sm font-semibold block mb-2">Itemized Ded.</label>
          <input
            type="number"
            inputMode="decimal"
            value={itemized}
            onChange={(e) => setItemized(e.target.value)}
            className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
            data-testid="input-itemized"
          />
        </div>
      </div>
      <button
        onClick={calculate}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate 2025 vs 2026"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pt-2">
        Federal only · Illustrative · Statutory projection
      </p>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="result-ready">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">
          Bottom Line
        </span>
        <h3 className="font-heading text-3xl md:text-4xl font-bold leading-tight">
          {result.comparison.annual_impact_message}
        </h3>
        <div className="grid grid-cols-2 gap-0 mt-6 border border-background/20">
          <div className="p-4 border-r border-background/20">
            <span className="font-mono text-[10px] uppercase tracking-widest text-background/60">2025 Take-Home</span>
            <div className="font-mono text-2xl tabular-nums font-semibold mt-1">{formatCurrency(result.year_2025.take_home)}</div>
          </div>
          <div className="p-4 bg-primary">
            <span className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/70">2026 Take-Home</span>
            <div className="font-mono text-2xl tabular-nums font-semibold mt-1">{formatCurrency(result.year_2026.take_home)}</div>
          </div>
        </div>
      </div>

      <ComparisonCard
        testid="bracket-comparison"
        rows={[
          { label: "Standard Deduction", value2025: result.year_2025.standard_deduction, value2026: result.year_2026.standard_deduction, format: "currency", invertImpact: true },
          { label: "Personal Exemption", value2025: result.year_2025.personal_exemption, value2026: result.year_2026.personal_exemption, format: "currency", invertImpact: true },
          { label: "Taxable Income", value2025: result.year_2025.taxable_income, value2026: result.year_2026.taxable_income, format: "currency" },
          { label: "Tax Before Credits", value2025: result.year_2025.tax_before_credits, value2026: result.year_2026.tax_before_credits, format: "currency" },
          { label: "Child Tax Credit", value2025: result.year_2025.child_tax_credit, value2026: result.year_2026.child_tax_credit, format: "currency", invertImpact: true },
          { label: "Marginal Rate", value2025: result.year_2025.marginal_rate, value2026: result.year_2026.marginal_rate, format: "percent" },
          { label: "Effective Rate", value2025: result.year_2025.effective_rate, value2026: result.year_2026.effective_rate, format: "percent" },
        ]}
        totals={{
          label: "Total Federal Tax",
          value2025: result.year_2025.total_tax,
          value2026: result.year_2026.total_tax,
          format: "currency",
        }}
      />

      <ComparisonChart
        testid="bracket-chart"
        title="Your tax picture: 2025 vs 2026"
        rows={[
          { label: "Taxable Inc.", v2025: result.year_2025.taxable_income, v2026: result.year_2026.taxable_income },
          { label: "Total Tax", v2025: result.year_2025.total_tax, v2026: result.year_2026.total_tax },
          { label: "Take-Home", v2025: result.year_2025.take_home, v2026: result.year_2026.take_home },
        ]}
      />

      <EmailGatePdfButton
        testid="bracket-pdf"
        filename="taxshift-2026-bracket-analysis.pdf"
        pdfPayload={{
          title: "2026 Federal Tax Bracket Analysis",
          subtitle: `Comparison for ${result.inputs.filing_status.replace("_", " ")} filer`,
          inputs: [
            { label: "Filing Status", value: result.inputs.filing_status.replace("_", " ").toUpperCase() },
            { label: "Gross Income", value: formatCurrency(result.inputs.gross_income) },
            { label: "Dependents", value: result.inputs.dependents },
            { label: "Itemized Deductions", value: formatCurrency(result.inputs.itemized_deductions) },
          ],
          headline: result.comparison.annual_impact_message,
          rows: [
            { label: "Standard Deduction", v2025: formatCurrency(result.year_2025.standard_deduction), v2026: formatCurrency(result.year_2026.standard_deduction) },
            { label: "Personal Exemption", v2025: formatCurrency(result.year_2025.personal_exemption), v2026: formatCurrency(result.year_2026.personal_exemption) },
            { label: "Taxable Income", v2025: formatCurrency(result.year_2025.taxable_income), v2026: formatCurrency(result.year_2026.taxable_income) },
            { label: "Tax Before Credits", v2025: formatCurrency(result.year_2025.tax_before_credits), v2026: formatCurrency(result.year_2026.tax_before_credits) },
            { label: "Child Tax Credit", v2025: formatCurrency(result.year_2025.child_tax_credit), v2026: formatCurrency(result.year_2026.child_tax_credit) },
            { label: "Marginal Rate", v2025: formatPercent(result.year_2025.marginal_rate), v2026: formatPercent(result.year_2026.marginal_rate) },
            { label: "Effective Rate", v2025: formatPercent(result.year_2025.effective_rate), v2026: formatPercent(result.year_2026.effective_rate) },
          ],
          totals: {
            label: "Total Federal Tax",
            v2025: formatCurrency(result.year_2025.total_tax),
            v2026: formatCurrency(result.year_2026.total_tax),
          },
        }}
      />
      <ResultExplainer slug="tax-bracket-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 md:p-16 text-center bg-card/50" data-testid="result-empty">
      <div className="max-w-md mx-auto">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4 block">
          Awaiting Calculation
        </span>
        <p className="font-heading text-2xl font-semibold mb-3">
          Enter your income, then click <span className="italic text-primary">Calculate</span>.
        </p>
        <p className="font-body text-sm text-muted-foreground">
          Your 2025 vs 2026 federal tax comparison will appear here.
        </p>
      </div>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 Federal Tax Bracket Calculator"
      slug="tax-bracket-2026"
      overline="Calculator · Income Tax · 2026 Brackets"
      description="Compare your 2025 and 2026 federal income tax liability using the real IRS tax brackets, with OBBBA's extra inflation bump on the 10% and 12% brackets factored in. Includes standard deduction, Child Tax Credit, and filing status."
      seoTitle="2026 Federal Tax Bracket Calculator — Real IRS Numbers"
      seoKeywords="2026 tax bracket calculator, federal income tax 2026, irs tax brackets, marginal tax rate 2026, 2026 vs 2025 tax"
      jsonLd={buildCalculatorSchema({
        name: "2026 Federal Tax Bracket Calculator",
        description: "Free calculator showing your 2026 federal income tax under OBBBA-era real IRS brackets and deductions.",
        url: "/calculator/tax-bracket-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            The 2026 brackets after OBBBA
          </h2>
          <div className="prose prose-stone max-w-none font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              The One Big Beautiful Bill Act (OBBBA) kept the 7-bracket TCJA structure — 10%, 12%,
              22%, 24%, 32%, 35%, 37% — permanent. On top of standard inflation adjustments for 2026,
              OBBBA added an <span className="font-semibold">extra inflation bump</span> specifically
              to the 10% and 12% brackets, giving middle-income earners a small but real tax cut.
            </p>
            <p>
              A single filer earning $55,000 stays in the 12% marginal bracket in 2026 (top of 12%
              bracket rose from $48,475 to $50,400). Combined with the standard deduction rising
              from $15,750 to $16,100, the total federal tax on that income drops meaningfully.
            </p>
            <p>
              Our calculator uses the real IRS 2025 and 2026 brackets (Revenue Procedures 2024-40
              and 2025-32) so the math is accurate down to the dollar.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "standard-deduction-2026", label: "Standard Deduction" },
        { slug: "child-tax-credit-2026", label: "Child Tax Credit" },
        { slug: "qbi-deduction-2026", label: "QBI Deduction" },
      ]}
    />
  );
};

export default TaxBracketCalculator;
